import React, { useState, useRef, useEffect } from 'react';
import '../../TaskManagement.css';

const API_BASE = process.env.REACT_APP_API_BASE !== undefined ? process.env.REACT_APP_API_BASE : 'http://localhost:3001';

function ResolveTaskModal({ task, onClose, onTaskResolved, bearerToken, refreshToken }) {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  const abortControllerRef = useRef(null);
  const successTimeoutRef = useRef(null);
  const currentTokenRef = useRef(bearerToken);

  // Keep token ref in sync
  useEffect(() => {
    currentTokenRef.current = bearerToken;
  }, [bearerToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Refresh the ThingsBoard token using the refresh token
  const refreshBearerToken = async () => {
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${API_BASE}/api/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return null;

      const newToken = response.headers.get('X-TB-Token');
      if (newToken) {
        currentTokenRef.current = newToken;
        return newToken;
      }
    } catch (err) {
      console.error('Token refresh failed:', err);
    }
    return null;
  };

  const handleConfirm = async () => {
    setIsResolving(true);
    setError('');

    // Abort any previous request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      // Call the resolve task webhook with alarmId
      const resolveApiUrl = process.env.REACT_APP_RESOLVE_TASK_API;
      if (!resolveApiUrl) {
        throw new Error('REACT_APP_RESOLVE_TASK_API is not defined in .env');
      }

      // Refresh token before resolve to ensure it's valid
      const freshToken = await refreshBearerToken() || currentTokenRef.current;

      const headers = {
        'Content-Type': 'application/json',
      };
      if (freshToken) {
        headers['Authorization'] = `Bearer ${freshToken}`;
      }

      const response = await fetch(`${resolveApiUrl}?alarmId=${task.id.id}`, {
        method: 'POST',
        headers,
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to resolve task: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Task resolved successfully:', result);

      setSuccess('Task marked as completed successfully!');
      setError('');

      // Close modal and update task after 1.5 seconds
      successTimeoutRef.current = setTimeout(() => {
        onTaskResolved(task.id);
      }, 1500);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error resolving task:', error);
      setError('Failed to resolve task. Please try again.');
      setSuccess('');
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Mark Task as Completed</h3>
        </div>

        <div className="modal-body">
          <div className="task-info-summary">
            <p className="task-type-summary">{task.type}</p>
            <p className="task-washroom-summary">{task.washroomLabel}</p>
          </div>

          {error && (
            <div className="modal-error" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="modal-success" role="alert">
              {success}
            </div>
          )}

          {!success && (
            <div>
              <p className="confirmation-message">
                Have you completed this task?
              </p>

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={onClose}
                  disabled={isResolving}
                >
                  No
                </button>
                <button
                  type="button"
                  className="modal-submit-btn"
                  onClick={handleConfirm}
                  disabled={isResolving}
                >
                  {isResolving ? 'Marking as Completed...' : 'Yes, Mark as Completed'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResolveTaskModal;
